import React, { useMemo, useRef, useCallback } from 'react'
import { Lightbulb, Compass, GitBranchPlus, Plus, Sparkles, Play, FileOutput } from 'lucide-react'
import { type NodeProps, Position } from '@xyflow/react'
import { DismissibleTooltip } from '../../common/DismissibleTooltip'
import { BaseNode } from './Base/BaseNode'
import type { DockAction } from './Base/BaseNodeTypes'
import { StakeholderRatingsFooter } from './stakeholderRating'
import { CATEGORY_STYLES } from '../../../constants/mindmapConfig'
import { useNodeActions } from '../../../context/nodeActions'
import { useIdeaActions } from '../IdeaActions'
import type { IdeaFlowNode } from '../../../types/mindmap-components'
import { useStableEdgeTopology } from '../hooks/useStableEdgeTopology'
import {
  LeftHandleWrapper,
  HandleLine,
  HandleButton,
  HandleButtonIcon,
  LeftSourceHandle,
} from './styles/HandleStyles'
import { useReadOnlyContext } from '../../../context/readOnly'
import { useFeatureAccessContext } from '../../../context/featureAccess'
import { NodeQualityChip, useNodeQualityScore } from '../../../features/nodeDataQuality'
import { ConfidenceBadge, useConfidenceScore } from '../../../features/confidenceBadge'

// UI text configuration
const UI_TEXT = {
  leftHandleTooltip: 'What problem does this solve?',
}

/**
 * Idea node component - for creative concepts and brainstorming
 */
export const IdeaNode = React.memo((props: NodeProps<IdeaFlowNode>) => {
  const { id, data } = props
  const nodeData = data
  const { isReadOnly } = useReadOnlyContext()
  const { isFeatureEnabled } = useFeatureAccessContext()
  const qualityResult = useNodeQualityScore(id)
  const {
    score: confidenceScore,
    isPending: isConfidencePending,
    scoreEntry: confidenceEntry,
  } = useConfidenceScore(id, nodeData.mindmapId)
  const categoryStyle = CATEGORY_STYLES['idea']
  const { handleAddParent, handleDirectChildCreation, handleGenerateRatings } = useNodeActions()
  const chatApiRef = useRef<{ autoGenerate: () => void } | null>(null)

  const handleChatReady = useCallback((api: { autoGenerate: () => void }) => {
    chatApiRef.current = api
  }, [])
  const { generateFigmaPromptForIdea, generateStoryboardForIdea, hasVentureAudienceForIdea } =
    useIdeaActions()
  // Use stable edge topology
  const edgeTopology = useStableEdgeTopology()

  // Ref for left handle wrapper
  const leftHandleWrapperRef = useRef<HTMLDivElement>(null)

  // Get unmet-need category style for the left handle (parent node type)
  const needCategoryStyle = CATEGORY_STYLES['need']

  // Check if node already has a parent (an edge where this node is the target)
  const hasParent = useMemo(() => {
    return edgeTopology.some((edge) => edge.target === id)
  }, [edgeTopology, id])

  // Check if this idea has a venture ancestor with audience defined (required for storyboard generation)
  const hasVentureAudience = useMemo(
    () => hasVentureAudienceForIdea(id),
    [hasVentureAudienceForIdea, id]
  )

  // Handle rating generation request
  // onlyForStakeholderNames is optional - when provided, only generates ratings for those stakeholders
  const handleRatingsClick = useCallback(
    (onlyForStakeholderNames?: string[]) => {
      void handleGenerateRatings(id, onlyForStakeholderNames)
    },
    [id, handleGenerateRatings]
  )

  // Render left handle for adding job parent
  const renderLeftHandle = useCallback(
    () => (
      <DismissibleTooltip title={UI_TEXT.leftHandleTooltip} mouseEnterDelay={0.5} placement="left">
        <LeftHandleWrapper
          ref={leftHandleWrapperRef}
          className="nodrag"
          onClick={(e) => {
            e.stopPropagation()
            handleAddParent(id, 'idea', leftHandleWrapperRef.current ?? undefined)
          }}
        >
          <HandleButton $accentColor={needCategoryStyle.iconColor}>
            <HandleButtonIcon>
              <Compass size={14} color={needCategoryStyle.iconColor} strokeWidth={2.5} />
            </HandleButtonIcon>
            <LeftSourceHandle type="source" position={Position.Left} id="left-source" />
          </HandleButton>
          <HandleLine $accentColor={needCategoryStyle.iconColor} $direction="left" />
        </LeftHandleWrapper>
      </DismissibleTooltip>
    ),
    [id, handleAddParent, needCategoryStyle.iconColor]
  )

  const renderHeaderActions = useCallback(
    () => (
      <>
        <ConfidenceBadge
          score={confidenceScore}
          isPending={isConfidencePending}
          nodeId={id}
          nodeLabel={nodeData.label}
          mindmapId={nodeData.mindmapId}
          scoreEntry={confidenceEntry}
        />
        {qualityResult ? (
          <NodeQualityChip qualityResult={qualityResult} nodeCategory="idea" inline />
        ) : null}
      </>
    ),
    [
      qualityResult,
      confidenceScore,
      isConfidencePending,
      confidenceEntry,
      id,
      nodeData.label,
      nodeData.mindmapId,
    ]
  )

  // Prototype sub-actions — rendered in the card footer bar dropdown
  const prototypeActions: DockAction[] = useMemo(
    () => [
      {
        id: 'storyboard',
        icon: Play,
        label: 'Create Visual Storyboard',
        group: 'prototype' as const,
        disabled: !hasVentureAudience,
        disabledReason: "Define the venture's 'Who is it for?' first to generate a storyboard",
        onClick: (e: React.MouseEvent) => {
          e.stopPropagation()
          void generateStoryboardForIdea(id)
        },
      },
      {
        id: 'export-ui-prompt',
        icon: FileOutput,
        label: 'Export UI Prompt',
        group: 'prototype' as const,
        onClick: (e: React.MouseEvent) => {
          e.stopPropagation()
          generateFigmaPromptForIdea(id)
        },
      },
    ],
    [id, hasVentureAudience, generateStoryboardForIdea, generateFigmaPromptForIdea]
  )

  // Dock actions: branch (dropdown)
  const dockActions: DockAction[] = useMemo(
    () => [
      {
        id: 'add-idea-group',
        icon: GitBranchPlus,
        label: 'Explore Further',
        group: 'branch' as const,
        onClick: () => {},
        dropdownChildren: [
          {
            id: 'add-own',
            icon: Plus,
            label: 'Add your own',
            subtitle: 'Type it yourself',
            group: 'branch' as const,
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation()
              handleDirectChildCreation(id, 'idea')
            },
          },
          {
            id: 'ai-suggest',
            icon: Sparkles,
            label: 'AI suggest',
            subtitle: 'Generate options',
            group: 'branch' as const,
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation()
              chatApiRef.current?.autoGenerate()
            },
          },
        ],
      },
    ],
    [id, handleDirectChildCreation]
  )

  return (
    <BaseNode
      id={props.id}
      nodeData={nodeData}
      icon={Lightbulb}
      categoryLabel="Idea"
      categoryDescription="Solutions and features that address user needs. Ideas can be rated by stakeholders to evaluate their viability."
      renderLeftHandle={hasParent ? undefined : renderLeftHandle}
      dockActions={dockActions}
      prototypeActions={prototypeActions}
      renderHeaderActions={renderHeaderActions}
      onChatReady={handleChatReady}
      renderNodeContent={
        isReadOnly || !isFeatureEnabled('simulated_stakeholders')
          ? undefined
          : () => (
              <StakeholderRatingsFooter
                ratings={nodeData.stakeholderRating}
                categoryStyle={categoryStyle}
                generatingNames={nodeData.generatingRatingNames ?? []}
                ideaLabel={nodeData.label}
                onGenerateRatings={handleRatingsClick}
              />
            )
      }
    />
  )
})
